import styled, { keyframes } from "styled-components";
import { SPACING, ANIMATION, OVERLAYS, QUERIES } from "../../utils/constants";
import { AmountVariant, amountVariantColor } from "../../utils/tone";
import Button, { BaseButton } from "../Button";

// ---- Full-screen mobile container ----
export const MobileScreen = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100vw;
  max-width: 100vw;
  background: var(--color-background-0);
  color: var(--color-text-default);
`;

export const MobileBody = styled.div`
  flex: 1;
  /* Allow the flex item to shrink below its content height so overflow-y
     actually scrolls instead of growing past the viewport and clipping. */
  min-height: 0;
  overflow-y: auto;
  padding: ${SPACING.md}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding-bottom: max(${SPACING.lg}px, env(safe-area-inset-bottom));

  /* The column flex layout is only used for spacing. Without this, cards are
     flex items that shrink (flex-shrink: 1) to fit the viewport instead of
     overflowing — squeezing cards with overflow:hidden (RowsCard) to near-zero
     and hiding their rows. Pin their size so the body scrolls instead. */
  & > * {
    flex-shrink: 0;
  }
`;

export const FormContents = styled.form`
  display: contents;
`;

// ---- Header ----
export const Header = styled.header`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: ${SPACING.md}px;
  padding-top: max(${SPACING.md}px, env(safe-area-inset-top));
  background: var(--color-card-background);
  border-bottom: 1px solid var(--color-border);
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  text-align: center;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-text-default);
`;

export const HeaderCancel = styled(BaseButton)`
  justify-self: start;
  background: none;
  border: none;
  padding: 0;
  font-size: var(--text-md);
  color: var(--color-text-secondary);
  cursor: pointer;
`;

export const HeaderAction = styled(BaseButton)`
  justify-self: end;
  background: none;
  border: none;
  padding: 0;
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-primary);
  cursor: pointer;
`;

// ---- Card ----
export const Card = styled.section`
  background: var(--color-card-background);
  border-radius: 16px;
  padding: ${SPACING.lg}px ${SPACING.md}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const CardLabel = styled.label`
  font-size: var(--text-xs);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

// Sets the CSS custom properties consumed by FormInput's `title` variant so the
// shared money input renders as the large, type-colored amount hero.
export const AmountInputWrapper = styled.div<{ $tone: AmountVariant }>`
  --input-title-color: ${({ $tone }) => amountVariantColor($tone)};
  --input-title-size: var(--text-display-lg);
  --input-title-underline: transparent;
`;

// ---- Rows ----
export const RowsCard = styled.section`
  background: var(--color-card-background);
  border-radius: 16px;
  overflow: hidden;
`;

export const Row = styled(BaseButton)<{ $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  width: 100%;
  min-height: 60px;
  padding: ${SPACING.md}px;
  background: ${({ $highlighted }) =>
    $highlighted ? "var(--color-info-background)" : "none"};
  border: none;
  border-bottom: 1px solid
    ${({ $highlighted }) =>
      $highlighted ? "var(--color-info-border)" : "var(--color-border)"};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }
`;

export const RowLabel = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  color: var(--color-text-secondary);
  flex-shrink: 0;
`;

export const RowValueGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  /* Take the remaining space and let the value truncate instead of pushing
     the row wider than the card (which clips under RowsCard overflow:hidden). */
  flex: 1;
  min-width: 0;
  justify-content: flex-end;

  & > svg {
    flex-shrink: 0;
  }
`;

export const RowValue = styled.span<{ $placeholder?: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $placeholder }) =>
    $placeholder ? "var(--color-text-secondary)" : "var(--color-text-default)"};
`;

export const RowError = styled.span`
  display: block;
  padding: 0 ${SPACING.md}px ${SPACING.sm}px;
  font-size: var(--text-sm);
  color: var(--color-error);
`;

// ---- Toggle row ----
export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border-bottom: 1px solid var(--color-border);
`;

export const HelperText = styled.span`
  display: block;
  padding: 0 ${SPACING.md}px ${SPACING.sm}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

// ---- Bottom sheets (pattern reused from TransactionActionSheet/styles.ts) ----
const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const sheetSlideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

export const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: ${OVERLAYS.backdropLight};
  animation: ${backdropFadeIn} ${ANIMATION.duration.normal}
    ${ANIMATION.easing.easeOut};
`;

export const Sheet = styled.div`
  background: var(--color-card-background);
  border-radius: 16px 16px 0 0;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  padding-bottom: max(${SPACING.sm}px, env(safe-area-inset-bottom));
  animation: ${sheetSlideUp} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOutQuint};
`;

export const SheetTitle = styled.h3`
  margin: 0;
  padding: ${SPACING.md}px;
  border-bottom: 1px solid var(--color-border);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-text-default);
`;

export const SheetOptions = styled.div`
  overflow-y: auto;
`;

export const SheetOption = styled(BaseButton)<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 52px;
  padding: ${SPACING.md}px;
  background: none;
  border: none;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  text-align: left;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  color: var(--color-text-default);
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};

  &:last-child {
    border-bottom: none;
  }
`;

export const SheetTextArea = styled.textarea`
  margin: ${SPACING.md}px;
  padding: ${SPACING.sm}px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-background-0);
  color: var(--color-text-default);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  resize: vertical;
  min-height: 96px;
`;

export const SheetInput = styled.input`
  margin: ${SPACING.md}px;
  padding: ${SPACING.sm}px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-background-0);
  color: var(--color-text-default);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
`;

export const SheetConfirm = styled(Button)`
  margin: 0 ${SPACING.md}px ${SPACING.md}px;
`;

export const SegmentedControl = styled.div`
  display: flex;
  background: var(--color-toggle-group-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  padding: ${SPACING.xs}px;
  gap: ${SPACING.xs}px;
`;

export const Segment = styled(BaseButton)<{ $active: boolean }>`
  justify-content: center;
  flex: 1;
  border: none;
  border-radius: 8px;
  padding: ${SPACING.sm}px;
  cursor: pointer;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  background: ${({ $active }) =>
    $active ? "var(--color-toggle-active-background)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--color-primary)" : "var(--color-text-secondary)"};
  box-shadow: ${({ $active }) => ($active ? "var(--shadow-small)" : "none")};
  transition:
    background ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};

  &[aria-pressed="false"]:hover:not(:disabled) {
    background: var(--color-toggle-hover-background);
    color: var(--color-text-default);
  }

  &[aria-pressed="true"]:disabled {
    opacity: 0.8;
  }

  @media not all and ${QUERIES.tabletAndUp} {
    min-height: 44px;
  }
`;

export const SelectPill = styled(BaseButton)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.xs}px;
  min-height: 44px;
  padding: ${SPACING.xs}px ${SPACING.md}px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-card-background);
  color: var(--color-text-default);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary);
  }

  svg {
    color: var(--color-text-secondary);
    font-size: var(--text-lg);
  }
`;
